"use client";

import clsx from "clsx";
import Link from "next/link";
import React, { type ComponentPropsWithoutRef, forwardRef } from "react";

export function H1({ children, className }: { children: React.ReactNode; className?: string }) {
  return <h1 className={`scroll-m-20 text-xl font-extrabold tracking-tight ${className}`}>{children}</h1>;
}

export function H2({ children, className }: { children: React.ReactNode; className?: string }) {
  return <h2 className={`text-lg font-semibold tracking-tight first:mt-0 ${className}`}>{children}</h2>;
}

export function H3({ children, className }: { children: React.ReactNode; className?: string }) {
  return <h3 className={`scroll-m-20 text-md font-semibold tracking-tight ${className}`}>{children}</h3>;
}

export function H4({ children, className }: { children: React.ReactNode; className?: string }) {
  return <h4 className={`scroll-m-20 text-base font-semibold tracking-tight ${className}`}>{children}</h4>;
}

export function P({ children, className }: { children: React.ReactNode; className?: string }) {
  return <p className={`leading-7 [&:not(:first-child)]:mt-6 ${className}`}>{children}</p>;
}

export function BlockQuote({ children, className }: { children: React.ReactNode; className?: string }) {
  return <blockquote className={`mt-6 border-l-2 pl-6 italic ${className}`}>{children}</blockquote>;
}

export function Lead({ children, className }: { children: React.ReactNode; className?: string }) {
  return <span className={`text-xl text-muted-foreground ${className}`}>{children}</span>;
}

export function Large({ children, className }: { children: React.ReactNode; className?: string }) {
  return <span className={`text-lg font-semibold tracking-tight ${className}`}>{children}</span>;
}

export function Medium({ children, className }: { children: React.ReactNode; className?: string }) {
  return <span className={`text-base font-semibold tracking-tight ${className}`}>{children}</span>;
}

export function Small({ children, className }: { children: React.ReactNode; className?: string }) {
  return <small className={`text-sm font-medium leading-none ${className}`}>{children}</small>;
}

export function ExtraSmall({ children, className }: { children: React.ReactNode; className?: string }) {
  return <span className={`text-xs font-medium leading-none ${className}`}>{children}</span>;
}

export function Muted({ children, className }: { children: React.ReactNode; className?: string }) {
  return <span className={`text-sm text-muted-foreground ${className}`}>{children}</span>;
}

export function highlightClass(useColoredLinks: boolean) {
  const backgroundColor = useColoredLinks ? "bg-primary/20" : "bg-accent";

  return `${backgroundColor} hover:${backgroundColor} rounded`;
}

export const Hyperlink = forwardRef<
  HTMLAnchorElement,
  ComponentPropsWithoutRef<typeof Link> & {
    isActive?: boolean;
    as?: typeof Link | "a";
  }
>(function Hyperlink({ children, className, isActive, as, href, ...rest }, ref) {
  const selectedClass = highlightClass(true);
  const Comp = as || Link;

  return (
    <Comp
      ref={ref}
      className={clsx(
        "text-sm rounded-md text-muted-foreground p-2",
        !isActive && "hover:bg-accent",
        isActive && selectedClass,
        className
      )}
      href={href as string}
      {...rest}
    >
      {children}
    </Comp>
  );
});
